<script lang="ts">

import type { PropType } from '@mimir/web/compat/vueCompat';
import { defineComponent } from '@mimir/web/compat/vueCompat';
import { VSelect } from 'vuetify/lib';
import { createVSelectToggleHandlers } from '@mimir/web/utils/vselect-toggle-helper';
import {
  createFocusBlurHandlers,
  createListenersWithFocusHandlers,
} from '@mimir/web/utils/m-input-common';
import type { VuetifyInputRef } from '@mimir/web/utils/m-input-common';
import MLabelRow from './MLabelRow.vue';


export default defineComponent({
  name: 'MSelect',
  components: {
    VSelect,
    MLabelRow,
  },
  inheritAttrs: false,
  props: {
    label: {
      type: String,
      default: undefined,
    },
    highContrast: {
      type: Boolean,
      default: false,
    },
    closeOnClick: {
      type: Boolean,
      default: true,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    compact: { // For inline selects that need to match smaller input sizes (e.g. in AdvancedSearchMenu)
      type: Boolean,
      default: false,
    },
    menuActive: {
      type: Boolean,
      default: false,
    },
    menuProps: {
      type: Object as PropType<Record<string, any>>,
      default: () => ({}),
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    flat: {
      type: Boolean,
      default: false,
    },
    solo: {
      type: Boolean,
      default: false,
    },
    v2Style: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isFocused: false,
      toggleHandlers: null as ReturnType<typeof createVSelectToggleHandlers> | null,
      focusHandlers: null as ReturnType<typeof createFocusBlurHandlers> | null,
    };
  },
  computed: {
    isDefaultStyle(): boolean {
      return !this.v2Style;
    },
    isPlain(): boolean {
      if (this.isDefaultStyle) return false;
      return this.plain || (!this.solo && !this.flat && !this.dense && !this.compact && !this.outlined);
    },
    selectListeners(): Record<string, unknown> {
      return createListenersWithFocusHandlers(this.$listeners, this.focusHandlers);
    },
    filteredSlots(): Record<string, unknown> {
      const { infotext, ...filtered } = this.$slots;
      return filtered;
    },
    defaultStyleAttrs(): Record<string, unknown> {
      const { flat, dense, solo, outlined, compact, plain, class: _class, ...rest } = this.$attrs;
      return rest;
    },
  },
  mounted() {
    this.setupHandlers();
  },
  beforeDestroy() {
    this.cleanupHandlers();
  },
  methods: {
    setupHandlers() {
      this.focusHandlers = createFocusBlurHandlers({
        setFocused: (value) => { this.isFocused = value; },
        getListeners: () => this.$listeners,
      });

      // Toggle handlers (close menu on click) - TODO: Remove after Vue 3 migration
      if (this.closeOnClick) {
        const toggleState = {
          shouldPreventClick: false,
          selectRef: null,
          getSelectRef: () => this.$refs.select,
        };
        this.toggleHandlers = createVSelectToggleHandlers(toggleState);
        this.toggleHandlers.attachListeners();
      }
    },
    cleanupHandlers() {
      if (this.toggleHandlers) {
        this.toggleHandlers.detachListeners();
        this.toggleHandlers = null;
      }
    },
    handleKeydown(event: KeyboardEvent) {
      if (event.key === 'Backspace') {
        // Only allow backspace to clear if the select is clearable
        const clearable = this.$attrs.clearable;
        const isClearable = (typeof clearable === 'boolean' && clearable) || (typeof clearable === 'string' && clearable === '');
        if (!isClearable) {
          return;
        }

        const selectRef = this.$refs.select as VuetifyInputRef | undefined;
        if (selectRef) {
          const attrValue = this.$attrs.value;
          const hasValue = attrValue !== undefined && attrValue !== null && attrValue !== '';
          const multiple = this.$attrs.multiple;
          const isMultiple = (typeof multiple === 'boolean' && multiple) || (typeof multiple === 'string' && multiple === '');
          const hasSearch = selectRef.isMenuActive && selectRef.search;

          if (hasValue && !hasSearch) {
            event.preventDefault();
            event.stopPropagation();

            if (isMultiple) {
              this.$emit('input', []);
            } else {
              this.$emit('input', null);
            }
          }
        }
      }
    },
    activateMenu() {
      const ref = this.$refs.select as VuetifyInputRef | undefined;
      ref?.activateMenu?.();
    },
    focus() {
      const ref = this.$refs.select as VuetifyInputRef | undefined;
      ref?.focus?.();
    },
    blur() {
      const ref = this.$refs.select as VuetifyInputRef | undefined;
      ref?.blur?.();
    },
    getIsMenuActive(): boolean {
      const ref = this.$refs.select as VuetifyInputRef | undefined;
      return ref?.isMenuActive ?? false;
    },
    setIsMenuActive(value: boolean) {
      const ref = this.$refs.select as VuetifyInputRef | undefined;
      if (ref && 'isMenuActive' in ref) {
        ref.isMenuActive = value;
      }
    },
    getSelectRef() {
      return this.$refs.select;
    },
  },
});
</script>

<template>
  <div>
    <MLabelRow
      v-if="isDefaultStyle ? label : (!isPlain && label && (!outlined || solo))"
      :label="label"
      :class="[isDefaultStyle ? 'm-default-style-label' : 'profile-label', isFocused && 'focused', highContrast && 'high-contrast', !isDefaultStyle && outlined && solo && 'muted']"
    >
      <template
        v-if="$slots.infotext"
        #infotext
      >
        <slot name="infotext" />
      </template>
    </MLabelRow>
    <div
      role="listbox"
      :aria-label="$attrs['aria-label'] ?? $attrs.ariaLabel ?? label"
    >
      <VSelect
      ref="select"
      :class="[
        isDefaultStyle ? 'm-select m-default-style' : (isPlain ? 'm-select' : 'm-select m-common'),
        !isDefaultStyle && label === undefined && 'm-select-inline',
        !isDefaultStyle && label !== undefined && outlined && solo && 'm-select-inline-label',
        !isDefaultStyle && compact && 'm-select-compact',
        menuActive && 'm-select-menu-active',
        $attrs.class
      ]"
      :aria-label="$attrs['aria-label'] ?? $attrs.ariaLabel ?? label"
      v-bind="$attrs"
      :dense="isDefaultStyle ? false : (dense || compact)"
      :flat="isDefaultStyle ? true : (flat || (!isPlain && label !== undefined && !outlined))"
      :hide-details="$attrs['hide-details'] ?? 'auto'"
      :label="isDefaultStyle ? undefined : (outlined && solo ? undefined : label)"
      :loader-height="1"
      :menu-props="{ ...menuProps, offsetY: true }"
      :outlined="isDefaultStyle ? false : outlined"
      :solo="isDefaultStyle ? true : (outlined ? false : solo || (!isPlain && label !== undefined && !outlined))"
      v-on="selectListeners"
      @keydown="handleKeydown"
    >
      <template
        v-for="(_, scopedSlotName) in $scopedSlots"
        #[scopedSlotName]="slotData"
      >
        <slot
          :name="scopedSlotName"
          v-bind="slotData"
        />
      </template>
      <template
        v-for="(_, slotName) in filteredSlots"
        #[slotName]
      >
        <slot :name="slotName" />
      </template>
    </VSelect>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.v-text-field {
  padding-top: 0 !important;
  margin-top: 0 !important;
}

.v-input {
  margin-bottom: 4px;

  :deep(.v-input__prepend-outer) {
    margin: 4px 4px 0 0 !important;
  }

  &.v-input--dense :deep(.v-input__prepend-outer) {
    .v-icon {
      font-size: 16px;
    }
  }

  :deep(.v-input__append-inner) {
    align-self: center;
    margin-top: 0px !important;
    .v-icon {
      margin-top: -3px;
      font-size: 24px !important;
    }
  }

  :deep(.v-input__append-outer) {
    font-size: 24px !important;
    margin-top: 4px !important;
  }

  :deep(.v-input__icon--append-outer) button {
    color: var(--v-strong-base) !important;
  }

  :deep(.v-input__control) {
    min-height: 24px !important;
  }

  &:not(.m-checkbox):not(.v-text-field--outlined):not(.m-default-style) :deep(.v-input__slot) {
    font-size: 12px;
    background: var(--v-inputBackground-base) !important;
    outline: 1px solid var(--v-inputBorder-lighten2);
    padding: 0 8px !important;
    border-radius: 5px !important;

    .theme--light & {
      background: white !important;
      outline: 1px solid var(--v-inputBorder-darken1);
    }
  }

  &.v-text-field--outlined :deep(.v-input__slot) {
    font-size: 12px;
    min-height: 32px !important;
  }

  // Prevent Vuetify's outlined focus from thickening fieldset border (1px→2px)
  // which shifts content. Keep 1px and rely on color change for focus indicator.
  &.v-text-field--outlined :deep(fieldset) {
    border-width: 1px !important;
  }

  &.v-input--is-focused:not(.v-text-field--outlined):not(.m-default-style) :deep(.v-input__slot) {
    background: var(--v-inputFocusBackground-base) !important;
  }

  &.error--text:not(.v-text-field--outlined) :deep(.v-input__slot) {
    outline: 1px solid var(--v-error-base);
  }

  :deep(input) {
    padding-top: 10px;
    text-overflow: ellipsis;
  }

  :deep(.v-input__icon--clear) .v-icon {
    opacity: 0;
    font-size: 18px !important;
  }

  &:hover :deep(.v-input__icon--clear) .v-icon {
    opacity: 1;
  }

  // Default style (matches MTextField solo look)
  &.m-default-style {
    :deep(.v-input__slot) {
      padding: 0px 8px !important;
      min-height: 32px !important;
      border-radius: 4px !important;
      background: rgba(255, 255, 255, 0.12) !important;
      border: none !important;
      font-size: 13px;
    }
    :deep(.v-input__slot::before),
    :deep(.v-input__slot::after) {
      display: none;
    }
    :deep(.v-input__append-inner) {
      align-self: center;
      margin-top: 0px !important;
      .v-icon {
        font-size: 24px !important;
        margin-top: 0 !important;
      }
    }
    :deep(.v-input__prepend-outer) {
      margin-top: 7px;
    }
    :deep(.v-label) {
      display: none;
    }
    :deep(.v-input__control) {
      min-height: 32px !important;
    }
    :deep(fieldset) {
      border: none !important;
    }
    :deep(input) {
      padding: 0 !important;
      max-height: 32px !important;
    }
    :deep(.v-select__selections) {
      padding: 0 4px !important;
      min-height: 32px !important;
      input {
        padding: 0 !important;
      }
    }
    &.v-input--is-focused :deep(.v-input__slot) {
      outline: 1px solid var(--v-primary-base) !important;
    }
    &.error--text :deep(.v-input__slot) {
      outline: 1px solid var(--v-error-base) !important;
    }
  }
}

.m-default-style-label {
  margin-bottom: 4px;
}

.v-input--is-focused:not(.v-input--is-readonly):not(.v-text-field--outlined):not(.m-default-style) {
  :deep(.v-input__slot) {
    outline: 1px solid var(--v-primary-base) !important;
  }
  :deep(.v-input__prepend-outer) .v-icon {
    color: var(--v-primary-base) !important;
  }
  :deep(.v-input__append-inner) .v-icon {
    color: var(--v-primary-base) !important;
  }
}

.m-select-menu-active {
  :deep(.v-input__slot) {
    outline: 1px solid var(--v-primary-base) !important;
  }
  :deep(.v-input__prepend-outer) .v-icon {
    color: var(--v-primary-base) !important;
  }
}

.m-select-inline {
  :deep(.v-input__slot) {
    border-radius: 5px !important;
  }
  :deep(.v-input__slot::before),
  :deep(.v-input__slot::after) {
    border-style: none !important;
  }
}

.m-select-compact {
  :deep(.v-input__append-inner) {
    align-self: center;
    margin-top: 0 !important;
    .v-icon {
      margin-top: 0 !important;
    }
  }
}

.v-input.v-input--dense {
  :deep(.v-select__selections input) {
    font-size: 12px !important;
  }
}

.m-select {
  :deep(.v-input__append-inner) {
    cursor: pointer;
  }

  &:not(.v-text-field--outlined) :deep(.v-input__append-inner) .v-icon {
    margin-top: 0 !important;
  }
}

.m-select:not(.m-common):not(.m-default-style):not(.v-text-field--outlined) {
  :deep(.v-input__slot) {
    background: transparent !important;
    border: none !important;
    outline: none !important;
    padding: 0 !important;
    font-size: 16px;
  }

  margin-top: 16px !important;
  margin-bottom: 16px;
}

.m-select.m-common.v-text-field--outlined {
  :deep(fieldset) {
    border: none !important;
  }
  &.v-input--is-focused :deep(fieldset) {
    border: 2px solid var(--v-primary-base) !important;
  }
  &.error--text :deep(fieldset) {
    border: 2px solid var(--v-error-base) !important;
  }
}

.m-select-inline-label {
  margin-bottom: 4px;

  :deep(.v-input__slot) {
    border: none !important;
    min-height: 32px !important;
    padding: 0 8px !important;
  }

  :deep(.v-text-field__details) {
    padding: 0 !important;
  }

  :deep(fieldset) {
    border: none !important;
  }
}
</style>
